import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { IconWrench, IconStar } from '../components/Icons';

const TechnicianDirectoryView = () => {
  const { t } = useLanguage();
  
  const [searchFilters, setSearchFilters] = useState({
    specialty: '',
    area: '',
  });

  const [technicians] = useState([
    {
      id: 1,
      name: 'Pedro Santos',
      serviceArea: ['Makati', 'BGC'],
      services: ['Plumbing', 'Electrical'],
      rating: 4.8,
      reviews: 124,
      price: 'Per hour ₱1,500~',
      phone: '0917-123-4567',
    },
    {
      id: 2,
      name: 'Juan Electric',
      serviceArea: ['Quezon City', 'Pasig'],
      services: ['Electrical', 'Aircon'],
      rating: 4.6,
      reviews: 89,
      price: 'Per hour ₱1,450~',
      phone: '0918-234-5678',
    },
    {
      id: 3,
      name: 'Carlos Repair',
      serviceArea: ['Mandaluyong', 'San Juan'],
      services: ['General Repairs', 'Plumbing'],
      rating: 4.9,
      reviews: 156,
      price: 'Per hour ₱1,400~',
      phone: '0919-345-6789',
    },
  ]);

  const specialties = [
    { value: '', label: t('allSpecialties') },
    { value: 'plumbing', label: t('plumbing') },
    { value: 'electrical', label: t('electrical') },
    { value: 'hvac', label: t('hvac') },
    { value: 'general', label: t('generalRepairs') },
  ];

  const areas = [
    { value: '', label: t('allAreas') },
    { value: 'makati', label: 'Makati' },
    { value: 'bgc', label: 'BGC' },
    { value: 'quezon', label: 'Quezon City' },
    { value: 'pasig', label: 'Pasig' },
  ];

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<IconStar key={i} className="w-4 h-4" fill="currentColor" />);
    }
    if (hasHalfStar) {
      stars.push(<IconStar key="half" className="w-4 h-4" fill="currentColor" style={{ opacity: 0.5 }} />);
    }
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<IconStar key={`empty-${i}`} className="w-4 h-4" />);
    }

    return stars;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('technicianDirectory')}</h1>

      {/* Search Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">{t('searchTechnicians')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('specialty')}
            </label>
            <select
              value={searchFilters.specialty}
              onChange={(e) => setSearchFilters({ ...searchFilters, specialty: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {specialties.map((specialty) => (
                <option key={specialty.value} value={specialty.value}>
                  {specialty.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('area')}
            </label>
            <select
              value={searchFilters.area}
              onChange={(e) => setSearchFilters({ ...searchFilters, area: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {areas.map((area) => (
                <option key={area.value} value={area.value}>
                  {area.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md">
              {t('search')}
            </button>
          </div>
        </div>
      </div>

      {/* Technician List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {technicians.map((technician) => (
          <div key={technician.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{technician.name}</h3>
                <p className="text-sm text-gray-600">
                  {t('serviceArea')}: {technician.serviceArea.join(', ')}
                </p>
              </div>
              <IconWrench className="w-6 h-6 text-gray-400" />
            </div>
            
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-1">{t('services')}:</p>
              <div className="flex flex-wrap gap-2">
                {technician.services.map((service, index) => (
                  <span key={index} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                    {service}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center mb-1">
                <div className="flex text-yellow-400">
                  {renderStars(technician.rating)}
                </div>
                <span className="ml-2 text-sm text-gray-600">
                  {technician.rating} ({technician.reviews} reviews)
                </span>
              </div>
              <p className="text-sm text-gray-600">{t('price')}: {technician.price}</p>
            </div>

            <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md">
              {t('contactTechnician')}
            </button>
          </div>
        ))}
      </div>

      {technicians.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500">{t('noSearchResults')}</p>
        </div>
      )}
    </div>
  );
};

export default TechnicianDirectoryView;
